import type { ReactNode } from 'react'
import { Check, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LOGIN_HERO_FEATURE_KEYS } from './login/login-hero-constants'

/** Subtle dot grid background for left hero panel */
function GpsDotsPattern() {
  return (
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
    />
  )
}

interface AuthHeroLayoutProps {
  children: ReactNode
}

/**
 * Split-screen auth wrapper.
 * Left: dark hero panel (hidden on mobile).
 * Right: white panel slot — pass form content as children.
 */
export function AuthHeroLayout({ children }: AuthHeroLayoutProps) {
  const { t } = useTranslation()

  return (
    <div className="flex h-screen">
      {/* Left hero panel — visible lg+ only */}
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-zinc-950 via-zinc-900 to-stone-900 flex-col p-10 relative overflow-hidden">
        <GpsDotsPattern />

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <MapPin size={18} className="text-white" />
          </div>
          <span className="text-white font-semibold text-lg">HeristepGPS Admin</span>
        </div>

        {/* Center content */}
        <div className="relative flex-1 flex flex-col justify-center">
          <p className="text-zinc-300 text-xl font-medium mb-8 max-w-xs leading-relaxed">
            {t('auth.heroTitle')}
          </p>
          <ul className="space-y-3">
            {LOGIN_HERO_FEATURE_KEYS.map((featureKey) => (
              <li key={featureKey} className="flex items-center gap-3 text-zinc-300 text-sm">
                <Check size={16} className="text-blue-400 shrink-0" />
                {t(featureKey)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right white panel */}
      <div className="flex-1 flex flex-col bg-white relative">{children}</div>
    </div>
  )
}
